import styled from "@emotion/styled";
import { Theme } from "@emotion/react";
import { ListBox } from "react-aria-components";
import { MDSTextFieldColor } from "../text-field/text-field.types";
import { MDSListBoxItem } from "../../lists/list-box-item";

export const StyledCombobox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  position: relative;
`;

export const StyledLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.grey.x700};
  line-height: ${({ theme }) => theme.lineHeights.small};
`;

export const StyledInputContainer = styled.div<{ color?: MDSTextFieldColor }>`
  display: flex;
  align-items: center;
  position: relative;
  border: 1px solid ${({ theme, color }) => getBorderColor(theme, color)};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.white};
  min-height: 40px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus-within {
    border-color: ${({ theme, color }) => getFocusBorderColor(theme, color)};
    box-shadow: 0 0 0 2px ${({ theme, color }) => getFocusShadowColor(theme, color)};
  }

  &:hover:not(:focus-within) {
    border-color: ${({ theme, color }) => getHoverBorderColor(theme, color)};
  }

  &[aria-disabled="true"] {
    background: ${({ theme }) => theme.colors.grey.x100};
    border-color: ${({ theme }) => theme.colors.grey.x300};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const StyledLeftContainer = styled.div<{ color?: MDSTextFieldColor }>`
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  border-right: 1px solid ${({ theme }) => theme.colors.grey.x200};
  min-height: 38px;

  &:empty {
    display: none;
  }
`;

export const StyledRightContainer = styled.div<{ color?: MDSTextFieldColor }>`
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  border-left: 1px solid ${({ theme }) => theme.colors.grey.x200};
  min-height: 38px;

  &:empty {
    display: none;
  }
`;

export const StyledLeftIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.grey.x400};
  }
`;

export const StyledActionButtonContainer = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  button {
    border: none;
    background: none;
    padding: 6px;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.grey.x100};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme, isActive }) =>
      isActive ? theme.colors.mblue.x600 : theme.colors.grey.x400};
    transition: color 0.2s ease;
  }
`;

export const StyledInputArea = styled.div`
  flex: 1;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  width: 100%;
  min-height: 40px;

  display: flex;
  align-items: center;
  flex-direction: row;
`;

export const StyledExpand = styled.div`
  flex: 1;
`;

export const StyledScrollable = styled.div({
  flex: 1,
  height: "fit-content",
  maxHeight: "120px",
  overflowY: "auto",
  "::-webkit-scrollbar": {
    display: "none",
  },
  "-ms-overflow-style": "none",
  "scrollbar-width": "none",
});

export const StyledTriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.grey.x100};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.grey.x400};
    transition: transform 0.2s ease;
  }
`;

export const StyledPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grey.x200};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.popover};
  max-height: 300px;
  overflow: hidden;
`;

export const StyledListBox = styled(ListBox)`
  padding: ${({ theme }) => theme.spacing.xs};
  max-height: 300px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey.x100};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grey.x300};
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.colors.grey.x400};
    }
  }
`;

export const StyledListBoxItem = styled(MDSListBoxItem)`
  box-sizing: border-box;

  &[data-hovered] {
    background: ${({ theme }) => theme.colors.mblue.x400};
  }

  &[data-focus-visible] {
    outline: 2px solid ${({ theme }) => theme.colors.mblue.x400};
    outline-offset: -1px;
  }
`;

export const StyledLoadingItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x500};
  justify-content: center;

  svg {
    animation: spin 1s linear infinite;
    width: 16px;
    height: 16px;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export const StyledErrorItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.red.x600};
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.red.x500};
  }
`;

export const StyledEmptyItem = styled.div`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x500};
  text-align: center;
  font-style: italic;
`;

export const StyledErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  color: ${({ theme }) => theme.colors.red.x600};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

// Helper functions for border colors
const getBorderColor = (theme: Theme, color?: MDSTextFieldColor) => {
  switch (color) {
    case "blue":
      return theme.colors.mblue.x300;
    case "error":
      return theme.colors.red.x300;
    default:
      return theme.colors.grey.x300;
  }
};

const getFocusBorderColor = (theme: Theme, color?: MDSTextFieldColor) => {
  switch (color) {
    case "blue":
      return theme.colors.mblue.x500;
    case "error":
      return theme.colors.red.x500;
    default:
      return theme.colors.mblue.x500;
  }
};

const getFocusShadowColor = (theme: Theme, color?: MDSTextFieldColor) => {
  switch (color) {
    case "blue":
      return `${theme.colors.mblue.x500}25`;
    case "error":
      return `${theme.colors.red.x500}25`;
    default:
      return `${theme.colors.mblue.x500}25`;
  }
};

const getHoverBorderColor = (theme: Theme, color?: MDSTextFieldColor) => {
  switch (color) {
    case "blue":
      return theme.colors.mblue.x400;
    case "error":
      return theme.colors.red.x400;
    default:
      return theme.colors.grey.x400;
  }
};
